import { defineStore } from "pinia"
import { ref, computed } from "vue"
import { useRouter } from "vue-router"
import set from "lodash/fp/set"
import compact from "lodash/fp/compact"
import isMatch from "lodash/fp/isMatch"

import WatchService from "@/services/resources/WatchService"
import CommentService from "@/services/resources/CommentService"
import CourseService from "@/services/resources/CourseService"
import CertifyService from "@/services/resources/CertifyService"
import useNotificationStore from "@/store/notification.store"
import { modal } from "@/events"
import { paths } from "@/utils/route-paths"

/** @type {WatchService} */
const watchService = WatchService.build()
const serviceCourse = CourseService.build()
const serviceComments = CommentService.build()

// --- Types ---

interface WatchParams {
  courseId?: number | null
  moduleId?: number | null
  lessonId?: number | null
}

interface WatchCourse {
  title: string
  [key: string]: unknown
}

interface WatchModule {
  id: number
  [key: string]: unknown
}

interface WatchLesson {
  id?: number
  course_id?: number
  module_id?: number
  is_completed?: boolean
  my_rating?: number
  audios?: Array<{ cdn_url?: string }>
  media?: { cdn_url?: string }
  source?: string
  [key: string]: unknown
}

interface LoadingState {
  course: boolean
  member: boolean
  lesson: boolean
  comments: boolean
  status?: boolean
  answer?: boolean
}

interface LessonStatus {
  is_completed?: boolean
  [key: string]: unknown
}

interface CourseStatus {
  lessons?: Record<number, LessonStatus>
  [key: string]: unknown
}

interface CommentCountStatus {
  all: number
  approveds: number
  unapproveds: number
  trash: number
  pending: number
}

interface ModFilters {
  page?: number
  searchString?: string
  status?: string
}

interface PaginatedResponse {
  data?: unknown[]
  [key: string]: unknown
}

export const useWatchStore = defineStore('watch', () => {
  const router = useRouter()

  // --- State ---
  const params = ref<WatchParams>({})
  const status = ref<CourseStatus>({})
  const loading = ref<LoadingState>({
    course: true,
    member: true,
    lesson: true,
    comments: true,
  })
  const course = ref<WatchCourse>({ title: "" })
  const modules = ref<WatchModule[]>([])
  const lesson = ref<WatchLesson>({})
  const module_ = ref<WatchModule | null>(null)
  const comments = ref<unknown[]>([])
  const prev = ref<WatchParams | null>(null)
  const next = ref<WatchParams | null>(null)
  const attachments = ref<unknown[]>([])

  // Moderation state
  const allComments = ref<PaginatedResponse>({})
  const approvedComments = ref<PaginatedResponse>({})
  const reprovedComments = ref<PaginatedResponse>({})
  const unapprovedComments = ref<PaginatedResponse>({})
  const trashComments = ref<PaginatedResponse>({})
  const currentModFilters = ref<ModFilters>({})
  const countStatusComments = ref<CommentCountStatus>({
    all: 0,
    approveds: 0,
    unapproveds: 0,
    trash: 0,
    pending: 0,
  })
  const lastLoadedCommentsModPage = ref<PaginatedResponse>({})

  // --- Getters (computed) ---
  const getParams = computed(() => params.value)
  const getCourse = computed(() => course.value)
  const getStatus = computed(() => status.value)
  const getLessonStatus = computed(() => (id: number): LessonStatus => status.value?.lessons?.[id] || {})
  const getLessonComments = computed(() => comments.value)
  const getModules = computed(() => modules.value)
  const getLoading = computed(() => loading.value)
  const getCurrentLesson = computed(() => lesson.value)
  const getCurrentModule = computed(() => module_.value)
  const getPrevLesson = computed(() => prev.value)
  const getNextLesson = computed(() => next.value)
  const getAllCommentsMod = computed(() => allComments.value)
  const getApprovedCommentsMod = computed(() => approvedComments.value)
  const getReprovedCommentsMod = computed(() => reprovedComments.value)
  const getUnapprovedCommentsMod = computed(() => unapprovedComments.value)
  const getTrashCommentsMod = computed(() => trashComments.value)
  const getCountStatusComments = computed(() => countStatusComments.value)
  const getLastLoadedCommentsModPage = computed(() => lastLoadedCommentsModPage.value)
  const getCurrentModFilters = computed(() => currentModFilters.value)

  // --- Internal helpers ---

  function clearState() {
    params.value = {}
    status.value = {}
    loading.value = { course: true, member: true, lesson: true, comments: true }
    course.value = { title: "" }
    modules.value = []
    lesson.value = {}
    module_.value = null
    comments.value = []
    prev.value = null
    next.value = null
    attachments.value = []
    allComments.value = {}
    approvedComments.value = {}
    reprovedComments.value = {}
    unapprovedComments.value = {}
    trashComments.value = {}
    currentModFilters.value = {}
    countStatusComments.value = { all: 0, approveds: 0, unapproveds: 0, trash: 0, pending: 0 }
    lastLoadedCommentsModPage.value = {}
  }

  // --- Actions ---

  async function load({ courseId, moduleId, lessonId }: { courseId: number; moduleId?: number; lessonId?: number }) {
    if (params.value?.courseId != courseId) {
      clearState()
    }

    const promises: Promise<unknown>[] = []

    if (
      params.value?.lessonId != lessonId ||
      params.value?.moduleId != moduleId ||
      params.value?.courseId != courseId
    ) {
      promises.push(loadCourseLesson({ courseId, moduleId, lessonId }))
    }

    if (params.value?.courseId != courseId) {
      promises.push(loadCoursePublic({ courseId }))
      promises.push(loadCourseStatus({ courseId }))
    }

    await Promise.all(promises)
  }

  async function loadCoursePublic({ courseId }: { courseId: number }) {
    try {
      const result = await watchService.read(`/public/course/${courseId}`)
      course.value = result.course
      modules.value = result.modules
      return result
    } finally {
      Object.assign(loading.value, { course: false })
    }
  }

  async function loadCourseStatus({ courseId }: { courseId: number }) {
    try {
      Object.assign(loading.value, { status: true })
      const statusResult = await watchService.read(`/status/course/${courseId}`)
      status.value = statusResult
    } finally {
      Object.assign(loading.value, { status: false })
    }
  }

  async function loadCourseLesson({ courseId, moduleId, lessonId }: { courseId: number; moduleId?: number; lessonId?: number }) {
    if (isMatch({ courseId, moduleId, lessonId }, params.value)) {
      return
    }

    const queryParams = compact([
      moduleId ? `moduleId=${moduleId}` : null,
      lessonId ? `lessonId=${lessonId}` : null,
    ]).join("&")
    const url = `/lesson/course/${courseId}${queryParams ? `?${queryParams}` : ''}`

    try {
      Object.assign(loading.value, { lesson: true })
      lesson.value = {}
      const result = await watchService.read(url)
      lesson.value = result.lesson
      module_.value = result.module
      prev.value = result.prev
      next.value = result.next
      params.value = {
        courseId: result.lesson.course_id,
        moduleId: result.lesson.module_id,
        lessonId: result.lesson.id,
      }
      router.replace(paths.watchV3(result.lesson.course_id, result.lesson.module_id, result.lesson.id) ?? '/home')
      return { lesson: result.lesson, prev: result.prev, next: result.next }
    } finally {
      Object.assign(loading.value, { lesson: false })
    }
  }

  async function doCompleteLesson() {
    const notificationStore = useNotificationStore()
    const { courseId, moduleId, lessonId } = params.value
    try {
      // Optimistic update
      lesson.value = { ...lesson.value, is_completed: true }
      const newStatus = set(['lessons', lessonId!, "is_completed"], true, status.value)
      status.value = newStatus

      await serviceCourse.postID({
        id: `${courseId}/module/${moduleId}/lesson/${lessonId}/progress`,
        completed: true,
      })
      await Promise.all([
        notificationStore.fetch(),
        loadCourseStatus({ courseId: courseId! }),
      ])

      // Check if certificate was generated
      try {
        const certificate = await serviceCourse.read(`${courseId}/certificate/me`)
        if (certificate && certificate.showed === 0) {
          // Check if it's an extension certificate with manual issuance
          // auto_issuance can be false, 'false', 0, '0', or 'off'
          const cfg = certificate.certificate_extension_config
          const isExtensionManualIssuance = cfg
            && (cfg.auto_issuance === false || cfg.auto_issuance === 'false' || cfg.auto_issuance === 0 || cfg.auto_issuance === '0' || cfg.auto_issuance === 'off')

          if (isExtensionManualIssuance) {
            // Show modal informing user to contact admin for certificate issuance
            modal.show("ModalCertificateAvailable", {})
          } else {
            // Show modal with download button for auto-issued certificates
            modal.show("ModalNewCertificate", {
              courseTitle: course.value?.title ?? '',
              certificateId: certificate.id,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any)
          }
          // Mark as showed
          const serviceCertificate = CertifyService.build()
          serviceCertificate.read("/notification").catch(() => {})
        }
      } catch { /* no certificate available */ }
    } catch (_err) {
      // Rollback
      lesson.value = { ...lesson.value, is_completed: false }
      const newStatus = set(['lessons', lessonId!, "is_completed"], false, status.value)
      status.value = newStatus
    }
  }

  async function doRatingLesson(rating: number) {
    const { courseId, moduleId, lessonId } = params.value
    lesson.value = { ...lesson.value, my_rating: rating }
    await serviceCourse.postID({
      id: `${courseId}/module/${moduleId}/lesson/${lessonId}/rating`,
      rating,
    })
  }

  async function doAnswerLessonQuiz(data: { question_id: number; [key: string]: unknown }) {
    const { courseId, moduleId, lessonId } = params.value
    try {
      Object.assign(loading.value, { answer: true })
      await serviceCourse.postID2({
        id: { id: `${courseId}/module/${moduleId}/lesson/${lessonId}/question/${data.question_id}/answer/student` },
        data,
      })
    } finally {
      Object.assign(loading.value, { answer: false })
    }
  }

  async function loadCommentList() {
    const { courseId, moduleId, lessonId } = params.value
    try {
      Object.assign(loading.value, { comments: true })
      const response = await serviceCourse.read(`${courseId}/module/${moduleId}/lesson/${lessonId}/comment`)
      comments.value = response.reverse()
    } finally {
      Object.assign(loading.value, { comments: false })
    }
  }

  async function doCommentLike({ id, courseId: cId, moduleId: mId, lessonId: lId }: { id: number; courseId?: number; moduleId?: number; lessonId?: number }) {
    const courseId = cId ?? params.value.courseId
    const moduleId = mId ?? params.value.moduleId
    const lessonId = lId ?? params.value.lessonId

    try {
      await serviceCourse.updateId({
        id: [courseId, "module", moduleId, "lesson", lessonId, "comment", id, "like"].join("/"),
      })
      if (params.value.courseId && params.value.moduleId && params.value.lessonId) {
        await loadCommentList()
      } else {
        await handleLoadCommentsModeration({
          page: currentModFilters.value.page,
          searchString: currentModFilters.value.searchString,
          status: currentModFilters.value.status,
        })
      }
    } catch {
      return
    }
  }

  async function doCommentUnlike({ id, courseId: cId, moduleId: mId, lessonId: lId }: { id: number; courseId?: number; moduleId?: number; lessonId?: number }) {
    const courseId = cId ?? params.value.courseId
    const moduleId = mId ?? params.value.moduleId
    const lessonId = lId ?? params.value.lessonId

    try {
      await serviceCourse.destroy({
        id: [courseId, "module", moduleId, "lesson", lessonId, "comment", id, "like"].join("/"),
      })
      if (params.value.courseId && params.value.moduleId && params.value.lessonId) {
        await loadCommentList()
      } else {
        await handleLoadCommentsModeration({
          page: currentModFilters.value.page,
          searchString: currentModFilters.value.searchString,
          status: currentModFilters.value.status,
        })
      }
    } catch {
      return
    }
  }

  async function doCommentCreate({ parent_lesson_comment_id, comment, courseId: cId, moduleId: mId, lessonId: lId }: {
    parent_lesson_comment_id?: number
    comment: string
    courseId?: number
    moduleId?: number
    lessonId?: number
  }) {
    const courseId = cId ?? params.value.courseId
    const moduleId = mId ?? params.value.moduleId
    const lessonId = lId ?? params.value.lessonId

    try {
      Object.assign(loading.value, { comments: true })
      await serviceCourse.postID({
        id: [courseId, "module", moduleId, "lesson", lessonId, "comment"].join("/"),
        comment,
        parent_lesson_comment_id,
      })
      if (params.value.courseId && params.value.moduleId && params.value.lessonId) {
        await loadCommentList()
      } else {
        await handleLoadCommentsModeration({
          page: currentModFilters.value.page,
          searchString: currentModFilters.value.searchString,
          status: currentModFilters.value.status,
        })
      }
    } finally {
      Object.assign(loading.value, { comments: false })
    }
  }

  async function doCommentUpdate({ id, comment, courseId: cId, moduleId: mId, lessonId: lId }: {
    id: number
    comment: string
    courseId?: number
    moduleId?: number
    lessonId?: number
  }) {
    const courseId = cId ?? params.value.courseId
    const moduleId = mId ?? params.value.moduleId
    const lessonId = lId ?? params.value.lessonId

    try {
      Object.assign(loading.value, { comments: true })
      await serviceCourse.update({
        id: [courseId, "module", moduleId, "lesson", lessonId, "comment", id].join("/"),
        comment,
      })
      if (params.value.courseId && params.value.moduleId && params.value.lessonId) {
        await loadCommentList()
      } else {
        await handleLoadCommentsModeration({
          page: currentModFilters.value.page,
          searchString: currentModFilters.value.searchString,
          status: currentModFilters.value.status,
        })
      }
    } finally {
      Object.assign(loading.value, { comments: false })
    }
  }

  async function doCommentApprove({ id }: { id: number }) {
    try {
      Object.assign(loading.value, { comments: true })
      await serviceComments.postID({ id, status: "published" })
      if (params.value.courseId && params.value.moduleId && params.value.lessonId) {
        await loadCommentList()
      } else {
        await handleLoadCommentsModeration({
          page: currentModFilters.value.page,
          searchString: currentModFilters.value.searchString,
          status: currentModFilters.value.status,
        })
        await actionLoadCommentCountStatus()
      }
    } finally {
      Object.assign(loading.value, { comments: false })
    }
  }

  async function doCommentReprove({ id }: { id: number }) {
    try {
      await serviceComments.postID({ id, status: "reproved" })
      if (params.value.courseId && params.value.moduleId && params.value.lessonId) {
        await loadCommentList()
      } else {
        await handleLoadCommentsModeration({
          page: currentModFilters.value.page,
          searchString: currentModFilters.value.searchString,
          status: currentModFilters.value.status,
        })
        await actionLoadCommentCountStatus()
      }
    } catch (_error) { /* intentional */
    }
  }

  async function doCommentDelete({ id, courseId: cId, moduleId: mId, lessonId: lId }: {
    id: number
    courseId?: number
    moduleId?: number
    lessonId?: number
  }) {
    const courseId = cId ?? params.value.courseId
    const moduleId = mId ?? params.value.moduleId
    const lessonId = lId ?? params.value.lessonId

    try {
      Object.assign(loading.value, { comments: true })
      await serviceCourse.destroy([courseId, "module", moduleId, "lesson", lessonId, "comment", id].join("/"))
      if (params.value.courseId && params.value.moduleId && params.value.lessonId) {
        await loadCommentList()
      } else {
        await handleLoadCommentsModeration({
          page: currentModFilters.value.page,
          searchString: currentModFilters.value.searchString,
          status: currentModFilters.value.status,
        })
        await actionLoadCommentCountStatus()
      }
    } finally {
      Object.assign(loading.value, { comments: false })
    }
  }

  async function loadTrashList({ page = 1 }: { page?: number }) {
    const url = "trash?page=" + page
    return await serviceComments.read(url)
  }

  async function doCommentRestore({ id }: { id: number }) {
    try {
      const data = { id: id + "/restore" }
      const resp = await serviceComments.postID(data)
      if (params.value.courseId && params.value.moduleId && params.value.lessonId) {
        await loadCommentList()
      } else {
        await handleLoadCommentsModeration({
          page: currentModFilters.value.page,
          searchString: currentModFilters.value.searchString,
          status: currentModFilters.value.status,
        })
        await actionLoadCommentCountStatus()
      }
      return resp
    } catch (_error) { /* intentional */
    }
  }

  async function loadAllCommentsModeration({ page, searchString }: { page?: number; searchString?: string }) {
    const queryParams = new URLSearchParams({
      page: page?.toString() ?? "1",
      ...(searchString ? { search_string: searchString } : {}),
    })
    const resp = await serviceComments.search(queryParams.toString())
    allComments.value = resp
    return resp
  }

  async function loadReprovedCommentsModeration({ page, searchString }: { page?: number; searchString?: string }) {
    const queryParams = new URLSearchParams({
      page: page?.toString() ?? "1",
      "status[]": "reproved",
      ...(searchString ? { search_string: searchString } : {}),
    })
    const resp = await serviceComments.search(queryParams.toString())
    reprovedComments.value = resp
    return resp
  }

  async function loadApprovedCommentsModeration({ page, searchString }: { page?: number; searchString?: string }) {
    const queryParams = new URLSearchParams({
      page: page?.toString() ?? "1",
      "status[]": "published",
      ...(searchString ? { search_string: searchString } : {}),
    })
    const resp = await serviceComments.search(queryParams.toString())
    approvedComments.value = resp
    return resp
  }

  async function loadUnapprovedCommentsModeration({ page, searchString }: { page?: number; searchString?: string }) {
    const queryParams = new URLSearchParams({
      page: page?.toString() ?? "1",
      "status[]": "draft",
      ...(searchString ? { search_string: searchString } : {}),
    })
    const resp = await serviceComments.search(queryParams.toString())
    unapprovedComments.value = resp
    return resp
  }

  async function loadTrashCommentsModeration({ page, searchString }: { page?: number; searchString?: string }) {
    const queryParams = new URLSearchParams({
      page: page?.toString() ?? "1",
      ...(searchString ? { search_string: searchString } : {}),
    })
    const resp = await serviceComments.read(`trash?${queryParams}`)
    trashComments.value = resp
    return resp
  }

  async function handleLoadCommentsModeration({ page, searchString, status: filterStatus }: { page?: number; searchString?: string; status?: string }) {
    Object.assign(loading.value, { comments: true })
    let resp: PaginatedResponse
    switch (filterStatus) {
      case "draft":
        resp = await loadUnapprovedCommentsModeration({ page, searchString })
        break
      case "published":
        resp = await loadApprovedCommentsModeration({ page, searchString })
        break
      case "reproved":
        resp = await loadReprovedCommentsModeration({ page, searchString })
        break
      case "trash":
        resp = await loadTrashCommentsModeration({ page, searchString })
        break
      default:
        resp = await loadAllCommentsModeration({ page, searchString })
        break
    }
    lastLoadedCommentsModPage.value = resp
    Object.assign(loading.value, { comments: false })
  }

  function actionSetModFilters({ page, searchString, status: filterStatus }: { page?: number; searchString?: string; status?: string }) {
    currentModFilters.value = { page, searchString, status: filterStatus }
  }

  async function actionLoadCommentCountStatus() {
    try {
      const { aprovados, lixeira, pendentes, reprovados, todos } = await serviceComments.read("categories")
      countStatusComments.value = {
        all: todos,
        approveds: aprovados,
        unapproveds: reprovados,
        trash: lixeira,
        pending: pendentes,
      }
    } catch (_error) { /* intentional */
    }
  }

  async function actionLoadFilteredCommentIds() {
    const queryParams = new URLSearchParams({
      ...(currentModFilters.value.status !== 'all' && currentModFilters.value.status !== 'trash'
        ? { 'status[]': currentModFilters.value.status ?? '' }
        : {}),
      ...(currentModFilters.value.searchString ? { search_string: currentModFilters.value.searchString } : {}),
      ...(currentModFilters.value.status === 'trash' ? { trashed: 'true' } : {}),
    })
    const resp = await serviceComments.read(`list-all-ids?${queryParams}`)
    return resp
  }

  async function actionHandleSelectedList({ selectedIds, status: bulkStatus }: { selectedIds: number[]; status: string }) {
    try {
      const data = { id: 'bulk-update', ids: selectedIds, status: bulkStatus }
      await serviceComments.postID(data)
      await handleLoadCommentsModeration({
        page: currentModFilters.value.page,
        searchString: currentModFilters.value.searchString,
        status: currentModFilters.value.status,
      })
      await actionLoadCommentCountStatus()
    } catch (_error) { /* intentional */
    }
  }

  function clear() {
    clearState()
  }

  return {
    // State
    params,
    status,
    loading,
    course,
    modules,
    lesson,
    module: module_,
    comments,
    prev,
    next,
    attachments,
    allComments,
    approvedComments,
    reprovedComments,
    unapprovedComments,
    trashComments,
    currentModFilters,
    countStatusComments,
    lastLoadedCommentsModPage,
    // Getters
    getParams,
    getCourse,
    getStatus,
    getLessonStatus,
    getLessonComments,
    getModules,
    getLoading,
    getCurrentLesson,
    getCurrentModule,
    getPrevLesson,
    getNextLesson,
    getAllCommentsMod,
    getApprovedCommentsMod,
    getReprovedCommentsMod,
    getUnapprovedCommentsMod,
    getTrashCommentsMod,
    getCountStatusComments,
    getLastLoadedCommentsModPage,
    getCurrentModFilters,
    // Actions
    load,
    loadCoursePublic,
    loadCourseStatus,
    loadCourseLesson,
    doCompleteLesson,
    doRatingLesson,
    doAnswerLessonQuiz,
    loadCommentList,
    doCommentLike,
    doCommentUnlike,
    doCommentCreate,
    doCommentUpdate,
    doCommentApprove,
    doCommentReprove,
    doCommentDelete,
    loadTrashList,
    doCommentRestore,
    loadAllCommentsModeration,
    loadReprovedCommentsModeration,
    loadApprovedCommentsModeration,
    loadUnapprovedCommentsModeration,
    loadTrashCommentsModeration,
    handleLoadCommentsModeration,
    actionSetModFilters,
    actionLoadCommentCountStatus,
    actionLoadFilteredCommentIds,
    actionHandleSelectedList,
    clear,
  }
})
